import Channel from '@/Enums/Channel.enum';
import Dimension from '@/Enums/ReportBuilder/Dimension.enum';
import Metric from '@/Enums/ReportBuilder/Metric.enum';
import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import ScheduleInterval from '@/Enums/ReportBuilder/ScheduleInterval.enum';
import TimeBucket from '@/Enums/ReportBuilder/TimeBucket.enum';
import type { IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';

export const FILTER_TAB_ID = 'report-builder-filter-tabs';
export const BUTTON_TAB_ID = 'reporting-api-tabs';

//#region DIMENSIONS
export const DIMENSION_LABELS: Record<Dimension, string> = {
  [Dimension.ORGANIZATION_NAME]: 'Organization',
  [Dimension.CLIENT_NAME]: 'Client',
  [Dimension.CLIENT_EXTERNAL_ID]: 'Client External ID',
  [Dimension.ADVERTISER_NAME]: 'Advertiser',
  [Dimension.ADVERTISER_ALT_ID]: 'Advertiser Alternative ID',
  [Dimension.CAMPAIGN_NAME]: 'Campaign Name',
  [Dimension.CAMPAIGN_ALT_ID]: 'Campaign Alternative ID',
  [Dimension.LINE_ITEM_NAME]: 'Line Item Name',
  [Dimension.LINE_ITEM_ALT_ID]: 'Line Item Alternative ID',
  [Dimension.CAMPAIGN_START_DATE]: 'Campaign Start Date',
  [Dimension.CAMPAIGN_END_DATE]: 'Campaign End Date',
  [Dimension.LINE_ITEM_START_DATE]: 'Line Item Start Date',
  [Dimension.LINE_ITEM_END_DATE]: 'Line Item End Date',
  [Dimension.CAMPAIGN_BOOKED_IMPRESSIONS]: 'Campaign Booked Impressions',
  [Dimension.LINE_ITEM_BOOKED_IMPRESSIONS]: 'Line Item Booked Impressions',
  [Dimension.CREATIVE_NAME]: 'Creative Name',
  [Dimension.CREATIVE_ID]: 'Creative ID',
  [Dimension.CREATIVE_DURATION]: 'Creative Duration',
  [Dimension.CREATIVE_FORMAT]: 'Creative Format',
  [Dimension.DIGITAL_CHANNEL]: 'Digital Channel',
  [Dimension.PUBLISHER_NAME]: 'Publisher Name',
  [Dimension.CONTENT_NETWORK]: 'Content Network',
  [Dimension.CONTENT_CHANNEL]: 'Content Channel',
  [Dimension.DEVICE_TYPE]: 'Device Type',
  [Dimension.CONTENT_GENRE]: 'Genre',
  [Dimension.CONTENT_TITLE]: 'Program',
  [Dimension.STATE]: 'State',
  [Dimension.DMA]: 'Market',
  [Dimension.CITY]: 'City',
  [Dimension.ZIP]: 'Zip Code',
  [Dimension.DAYPART]: 'Daypart',
  [Dimension.DAY_OF_WEEK]: 'Day of Week',
  [Dimension.HOUR_OF_DAY]: 'Hour of Day',
  [Dimension.RATE_CARD]: 'Rate Card CPM',
  // Label only — the dimension key stays `sold_cpm` (#4446).
  [Dimension.SOLD_CPM]: 'Client CPM',
  // [Dimension.AUDIENCE]: 'Audience',
  [Dimension.EVENT_ID]: 'Event ID',
  [Dimension.EVENT_NAME]: 'Event Name',
};

export const TAB_LABELS: Record<CustomReportChannel, string> = {
  [CustomReportChannel.Beeswax]: 'Streaming TV, OLV, Display, Audio',
  [CustomReportChannel.Youtube]: 'YouTube',
  [CustomReportChannel.Omniplatform]: 'Omni-Platform',
};

const createDimensionOption = (value: Dimension): IControlOption<string, Dimension> => ({
  value,
  label: DIMENSION_LABELS[value],
});

export const hydrateOptionGroup = (
  options: Dimension[],
  groupLabel: string,
): IGroupedControlOption<string, Dimension>[] => {
  return options.map(option => ({ ...createDimensionOption(option), groupLabel }));
};
//#endregion

//#region METRICS
export const METRIC_LABELS: Record<Metric, string> = {
  [Metric.IMPRESSIONS]: 'Impressions',
  [Metric.REACH]: 'Reach',
  [Metric.FREQUENCY]: 'Frequency',
  [Metric.VIDEO_COMPLETES]: 'Completed Views',
  [Metric.CLICKS]: 'Clicks',
  [Metric.VCR]: 'Video Completion Rate (VCR)',
  [Metric.VIDEO_Q1S]: 'Video: 25% Completes',
  [Metric.VIDEO_MIDPOINTS]: 'Video: 50% Completes',
  [Metric.VIDEO_Q3S]: 'Video: 75% Completes',
  [Metric.CTR]: 'Click Through Rate (CTR)',
  [Metric.IP_CONVERSIONS]: 'Conversions',
  [Metric.VIDEO_PLAYS]: 'Video Plays',
  [Metric.AUDIO_PLAYS]: 'Audio Plays',
  [Metric.AUDIO_COMPLETES]: 'Audio Completes',
  [Metric.AUDIO_Q1S]: 'Audio: 25% Completes',
  [Metric.AUDIO_MIDPOINTS]: 'Audio: 50% Completes',
  [Metric.AUDIO_Q3S]: 'Audio: 75% Completes',
  [Metric.AUDIO_SKIPS]: 'Audio Skips',
  [Metric.ACR]: 'Audio Completion Rate (ACR)',
  [Metric.BOOKED]: 'Booked',
  [Metric.PACING_TO]: 'Pacing To',
  [Metric.PACING_TO_PERCENT]: 'Pacing To %',
  [Metric.CLIENT_SPEND]: 'Client Spend',
  [Metric.TOTAL_SPEND]: 'Total Spend',
  [Metric.CLIENT_SPEND_CPA]: 'Client Spend CPA',
  [Metric.TOTAL_SPEND_CPA]: 'Total Spend CPA',
  [Metric.SPEND]: 'Spend',
  [Metric.CPM]: 'CPM',
};

export const createMetricOption = (value: Metric): IControlOption<string, Metric> => ({
  value,
  label: METRIC_LABELS[value],
});

export const hydrateMetricOptionGroup = (
  options: Metric[],
  groupLabel: string,
): IGroupedControlOption<string, Metric>[] => {
  return options.map(option => ({ ...createMetricOption(option), groupLabel }));
};
//#endregion

export const MOCK_ROWS = [
  {
    [TimeBucket.DAY]: '2024-01-29',
    [TimeBucket.WEEK]: '2024_5',
    [TimeBucket.MONTH]: '2024_1',
    [Dimension.CLIENT_NAME]: 'Generic Client',
    [Dimension.CLIENT_EXTERNAL_ID]: 'Client External ID',
    [Dimension.ADVERTISER_NAME]: 'Placeholder Advertiser',
    [Dimension.ADVERTISER_ALT_ID]: 'Advertiser Alt ID',
    [Dimension.CAMPAIGN_NAME]: 'Placeholder Campaign',
    [Dimension.CAMPAIGN_ALT_ID]: 'Campaign Alt ID',
    [Dimension.LINE_ITEM_NAME]: 'Placeholder Line Item',
    [Dimension.LINE_ITEM_ALT_ID]: 'Line Item Alt ID',
    [Dimension.CAMPAIGN_START_DATE]: '2024-01-26 05:00:00.000 Z',
    [Dimension.CAMPAIGN_END_DATE]: '2024-01-28 05:00:00.000 Z',
    [Dimension.LINE_ITEM_START_DATE]: '2024-01-26 05:00:00.000 Z',
    [Dimension.LINE_ITEM_END_DATE]: '2024-01-28 05:00:00.000 Z',
    [Dimension.CAMPAIGN_BOOKED_IMPRESSIONS]: 1000,
    [Dimension.LINE_ITEM_BOOKED_IMPRESSIONS]: 1000,
    [Dimension.CREATIVE_NAME]: 'BW Creative	1',
    [Dimension.CREATIVE_ID]: '124',
    [Dimension.CREATIVE_DURATION]: 0,
    [Dimension.CREATIVE_FORMAT]: 0,
    [Dimension.DIGITAL_CHANNEL]: Channel.OLV,
    [Dimension.PUBLISHER_NAME]: 'Placeholder Publisher',
    [Dimension.CONTENT_NETWORK]: 'Placeholder Network',
    [Dimension.CONTENT_CHANNEL]: 'Placeholder Channel',
    [Dimension.DEVICE_TYPE]: 'Mobile',
    [Dimension.CONTENT_GENRE]: 'Genre',
    [Dimension.CONTENT_TITLE]: 'Program',
    [Dimension.STATE]: 'USA/TX',
    [Dimension.DMA]: 'Dallas-Ft. Worth, TX',
    [Dimension.CITY]: 'Dallas, TX',
    [Dimension.ZIP]: '75098',
    [Dimension.DAYPART]: 'Late Fringe',
    [Dimension.DAY_OF_WEEK]: 'Mon',
    [Dimension.HOUR_OF_DAY]: 23,
    [Dimension.RATE_CARD]: 10.05,
    [Dimension.SOLD_CPM]: 10.05,
    // [Dimension.AUDIENCE]: 'Whomever',
    [Dimension.EVENT_ID]: 1001,
    [Dimension.EVENT_NAME]: 'Test event',
    [Metric.IMPRESSIONS]: 30,
    [Metric.REACH]: 20,
    [Metric.FREQUENCY]: 1.5,
    [Metric.VIDEO_COMPLETES]: 10,
    [Metric.CLICKS]: 15,
    [Metric.VCR]: 0.75,
    [Metric.VIDEO_Q1S]: 20,
    [Metric.VIDEO_MIDPOINTS]: 15,
    [Metric.VIDEO_Q3S]: 7,
    [Metric.CTR]: 0.000145487,
    [Metric.IP_CONVERSIONS]: 0,
    [Metric.VIDEO_PLAYS]: 15,
    [Metric.AUDIO_PLAYS]: 15,
    [Metric.AUDIO_COMPLETES]: 10,
    [Metric.AUDIO_Q1S]: 20,
    [Metric.AUDIO_MIDPOINTS]: 15,
    [Metric.AUDIO_Q3S]: 7,
    [Metric.AUDIO_SKIPS]: 1,
    [Metric.ACR]: 0.75,
    [Metric.CLIENT_SPEND]: 0.3,
    [Metric.TOTAL_SPEND]: 0.45,
    [Metric.CLIENT_SPEND_CPA]: 0.15,
    [Metric.TOTAL_SPEND_CPA]: 0.2,
    [Metric.SPEND]: 0.36,
    [Metric.CPM]: 12,
  },
];

export const BASE_TAB_ID = 'report-builder-filter-tab';

export const INTERVAL_LABELS: Record<ScheduleInterval, string> = {
  [ScheduleInterval.Daily]: 'Daily',
  [ScheduleInterval.Weekly]: 'Weekly',
  [ScheduleInterval.Monthly]: 'Monthly',
};

export const TIME_LABELS: Record<TimeBucket, string> = {
  [TimeBucket.DAY]: 'Day',
  [TimeBucket.WEEK]: 'Week',
  [TimeBucket.MONTH]: 'Month',
};
